import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  CellLeftV8,
  order2Sorting,
  TableCellV8,
  FormCol3,
  EditCellV8,
  CellCenterV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  CellDateTimeV8,
  CellDateV8,
  EyeColumnV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  isActiveColumn,
  lastDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  dateColumn,
  dateExpiryColumn,
} from 'shared-pages/common/grid-helpfunc';
import { FieldMultiMaterialDlg } from 'shared-pages/components';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { useGetContributionPeriodAsOptionsQuery } from '../../components/fs-contributionperiodgroup';
import { useGetContributorAsOptionsQuery } from '../../components/fs-contributorgroup';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateContributor] = useMutation(gqlUpdate);
  const [addNewContributor] = useMutation(gqlAdd);
  const [deleteContributor] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Contributor'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'name',
        header: t('Επωνυμία'),
        columnName: t('Επωνυμία'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'addressLine1',
        header: t('Διεύθυνση'),
        columnName: t('Διεύθυνση'),
        accessorKey: 'addressLine1',
        orderByField: 'addressLine1',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'phoneNumber1',
        header: t('Τηλέφωνο'),
        columnName: t('Τηλέφωνο'),
        accessorKey: 'phoneNumber1',
        orderByField: 'phoneNumber1',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'vatNumber',
        header: t('Α.Φ.Μ.'),
        columnName: t('Α.Φ.Μ.'),
        accessorKey: 'vatNumber',
        orderByField: 'vatNumber',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'taxOffice',
        header: t('Δ.Ο.Υ.'),
        columnName: t('Δ.Ο.Υ.'),
        accessorKey: 'taxOffice',
        orderByField: 'taxOffice',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'producerRegNum',
        header: t('ΑΜΠ'),
        columnName: t('ΑΜΠ'),
        accessorKey: 'producerRegNum',
        orderByField: 'producerRegNum',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },

      {
        id: 'cntContributionsPeriodgroup.name',
        header: t('Εισφορές'),
        columnName: t('Εισφορές'),
        accessorKey: 'contributionPeriodGroupId',
        orderByField: 'cntContributionsPeriodgroup.name',
        cell: TableCellV8,
        size: 50,
        meta: {
          type: 'select',
          hook: useGetContributionPeriodAsOptionsQuery,
          //where: collectorsWhere,
          idKey: 'contributionPeriodGroupId',
          idName: 'cntContributionsPeriodgroup.name',
        },
      },
      {
        id: 'cntContributorsGroup.name',
        header: t('Κατηγορία Υπόχρεου'),
        columnName: t('Κατηγορία Υπόχρεου'),
        accessorKey: 'contributorGroupId',
        orderByField: 'cntContributorsGroup.name',
        cell: TableCellV8,
        size: 50,
        meta: {
          type: 'select',
          hook: useGetContributorAsOptionsQuery,
          //where: collectorsWhere,
          idKey: 'contributorGroupId',
          idName: 'cntContributorsGroup.name',
        },
      },
      isActiveColumn(t, i18n),
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        orderByField: 'lastUser',
        size: 120,
        cell: ({ row, table }) => {
          return true ? EditCellV8({ row, table }) : <>-</>;
        },
      },
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="Contributors-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
            };
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Contributors - OnSaveRow row', row, changes);
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewContributor({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addCntContributor.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateContributor({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε τον Υπόχρεο <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteContributor({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε ο Υπόχρεος');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
